import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, User, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showInitButton, setShowInitButton] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast.error("Masukkan username dan password");
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/admin/login`, {
        username: username.trim(),
        password: password,
      });
      
      localStorage.setItem("admin_token", response.data.access_token);
      localStorage.setItem("admin_name", response.data.admin_name);
      
      toast.success(`Selamat datang, ${response.data.admin_name}!`);
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login gagal");
      // Tampilkan tombol init jika login gagal (mungkin belum ada admin)
      if (err.response?.status === 401) {
        setShowInitButton(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const initAdmin = async () => {
    try {
      const response = await axios.post(`${API}/admin/init`);
      toast.success(response.data.message);
      if (response.data.username) {
        setUsername(response.data.username);
        setShowInitButton(false); // Sembunyikan tombol setelah berhasil
      }
      if (response.data.message === "Admin already exists") {
        setShowInitButton(false); // Sembunyikan jika admin sudah ada
      }
    } catch (err) {
      toast.error("Gagal inisialisasi admin");
    }
  };

  // Cek apakah perlu tampilkan tombol init (hanya jika login gagal)
  const handleLoginError = () => {
    setShowInitButton(true);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <motion.img 
            src="/icons/icon-192x192.png" 
            alt="Logo" 
            className="w-16 h-16 rounded-2xl mx-auto mb-4"
            initial={{ rotate: -10, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          />
          <h1 className="text-2xl font-heading font-bold text-white mb-2">
            Admin Produk Digital
          </h1>
          <p className="text-slate-400">
            Masuk ke panel administrasi
          </p>
          <p className="text-xs text-slate-500 mt-2">
            URL: /admin/login
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <Card className="bg-slate-800 border-slate-700 card-hover">
            <CardContent className="p-6">
              <form onSubmit={handleLogin} className="space-y-5">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Masukkan username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="input-animated h-12 pl-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-sky-500"
                      data-testid="admin-username-input"
                    />
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Masukkan password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-animated h-12 pl-12 pr-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-sky-500"
                      data-testid="admin-password-input"
                    />
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 btn-flow btn-animated rounded-xl font-semibold"
                    data-testid="admin-login-btn"
                  >
                    {isLoading ? <div className="spinner w-5 h-5" /> : "Login"}
                  </Button>
                </motion.div>
              </form>

              {/* Footer - removed init button for security */}
              <motion.div 
                className="mt-6 pt-6 border-t border-slate-700"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <p className="text-xs text-slate-500 text-center">
                  Hubungi administrator jika lupa password
                </p>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div 
          className="mt-6 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Kembali ke Beranda
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
}
